import { useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import Layout from "@/components/feature/Layout";
import { Seo, JsonLd } from "@/components/base/Seo";
import Breadcrumb from "@/components/base/Breadcrumb";
import VerdictBadge from "@/components/feature/VerdictBadge";
import { articles, type Article } from "@/mocks/articles";

const toc = [
  { id: "conclusion", label: "鉴定结论" },
  { id: "background", label: "事件背景" },
  { id: "timeline", label: "时间线" },
  { id: "responses", label: "后续回应" },
  { id: "evidence", label: "证据与信源" },
];

function Related({ current }: { current: Article }) {
  const others = articles.filter((a) => a.id !== current.id).slice(0, 4);
  return (
    <div className="rounded-lg border border-background-200 bg-background-50 p-5">
      <h3 className="flex items-center gap-2 font-heading text-base font-600 text-foreground-950">
        <i className="ri-links-line text-primary-600"></i>
        相关鉴定
      </h3>
      <ul className="mt-4 space-y-4">
        {others.map((a) => (
          <li key={a.id}>
            <Link to={`/article/${a.id}`} className="group block">
              <span className="line-clamp-2 text-sm font-500 leading-snug text-foreground-800 transition-colors group-hover:text-accent-700">
                {a.title}
              </span>
              <span className="mt-1 block text-xs text-foreground-500">
                {a.category} · {a.date}
              </span>
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
}

export default function ArticlePage() {
  const { id } = useParams();
  const article = articles.find((a) => a.id === id);

  useEffect(() => {
    window.scrollTo({ top: 0 });
  }, [id]);

  if (!article) {
    return (
      <Layout>
        <div className="mx-auto flex w-full max-w-3xl flex-col items-center px-4 py-24 text-center md:px-8">
          <span className="flex h-16 w-16 items-center justify-center rounded-full bg-background-100 text-foreground-500">
            <i className="ri-emotion-sad-line text-3xl"></i>
          </span>
          <h1 className="mt-5 font-heading text-2xl font-700 text-foreground-950">这条鉴定记录不存在</h1>
          <p className="mt-3 text-sm text-foreground-600">可能是链接有误，或者该条目已被合并或更正。</p>
          <Link
            to="/verify"
            className="mt-6 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-500 text-background-50 transition-colors hover:bg-primary-600"
          >
            返回鉴定专区
          </Link>
        </div>
      </Layout>
    );
  }

  const articleLd = {
    "@context": "https://schema.org",
    "@type": "NewsArticle",
    headline: article.title,
    description: article.summary,
    datePublished: article.date,
    dateModified: article.date,
    inLanguage: "zh-CN",
    articleSection: article.category,
    keywords: article.tags.join(","),
    image: article.cover,
    author: { "@type": "Organization", name: article.author },
    publisher: { "@type": "Organization", name: "17爆料网" },
    mainEntityOfPage: `https://dahuaqi.cn/article/${article.id}`,
  };

  const crumbLd = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "首页", item: "https://dahuaqi.cn/" },
      { "@type": "ListItem", position: 2, name: "真假鉴定专区", item: "https://dahuaqi.cn/verify" },
      { "@type": "ListItem", position: 3, name: article.title, item: `https://dahuaqi.cn/article/${article.id}` },
    ],
  };

  return (
    <Layout>
      <Seo
        title={`${article.title} - 17爆料网真假鉴定`}
        description={article.summary}
        keywords={`${article.category},17爆料网,${article.tags.join(",")}`}
        path={`/article/${article.id}`}
        image={article.cover}
        type="article"
      />
      <JsonLd data={[articleLd, crumbLd]} />

      <div className="mx-auto w-full max-w-7xl px-4 pt-8 md:px-8">
        <Breadcrumb
          items={[
            { label: "真假鉴定专区", to: "/verify" },
            { label: article.category, to: `/verify?category=${article.category}` },
            { label: "正文" },
          ]}
        />
      </div>

      <div className="mx-auto grid w-full max-w-7xl gap-10 px-4 py-8 md:px-8 md:py-10 lg:grid-cols-3">
        <article className="lg:col-span-2">
          <div className="flex flex-wrap items-center gap-2">
            <VerdictBadge verdict={article.verdict} size="md" />
            <Link
              to={`/verify?category=${article.category}`}
              className="rounded-full bg-background-100 px-3 py-1 text-xs text-foreground-700 transition-colors hover:bg-background-200"
            >
              {article.category}
            </Link>
          </div>

          <h1 className="mt-4 font-heading text-2xl font-800 leading-snug text-foreground-950 md:text-4xl">
            {article.title}
          </h1>

          <div className="mt-5 flex flex-wrap items-center gap-x-5 gap-y-2 border-b border-background-200 pb-5 text-xs text-foreground-600 md:text-sm">
            <span className="flex items-center gap-1.5">
              <i className="ri-user-voice-line"></i>
              {article.author}
            </span>
            <span className="flex items-center gap-1.5">
              <i className="ri-calendar-line"></i>
              {article.date}
            </span>
            <span className="flex items-center gap-1.5">
              <i className="ri-time-line"></i>
              阅读约 {article.readTime}
            </span>
            <span className="flex items-center gap-1.5">
              <i className="ri-fire-line text-primary-500"></i>
              {article.heat} 关注
            </span>
          </div>

          <div className="mt-6 h-56 w-full overflow-hidden rounded-lg border border-background-200 md:h-80">
            <img src={article.cover} alt={article.coverAlt} className="h-full w-full object-top" />
          </div>

          <figure className="mt-3 text-xs text-foreground-500">{article.coverAlt}</figure>

          <section id="conclusion" className="mt-10 scroll-mt-24">
            <div className="rounded-lg border-l-4 border-primary-500 bg-primary-50 p-5 md:p-6">
              <h2 className="flex items-center gap-2 font-heading text-lg font-700 text-foreground-950">
                <i className="ri-shield-check-line text-primary-600"></i>
                鉴定结论
              </h2>
              <p className="mt-3 text-sm leading-relaxed text-foreground-800 md:text-base">{article.conclusion}</p>
            </div>
          </section>

          <section id="background" className="mt-10 scroll-mt-24">
            <h2 className="font-heading text-xl font-700 text-foreground-950 md:text-2xl">事件背景</h2>
            <p className="mt-4 text-sm leading-relaxed text-foreground-700 md:text-base">{article.background}</p>
          </section>

          <section id="timeline" className="mt-10 scroll-mt-24">
            <h2 className="font-heading text-xl font-700 text-foreground-950 md:text-2xl">事件时间线</h2>
            <ol className="mt-6 space-y-0">
              {article.timeline.map((ev, idx) => (
                <li key={ev.date} className="relative flex gap-4 pb-6 last:pb-0">
                  <span className="flex flex-col items-center">
                    <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-accent-100 font-label text-xs font-600 text-accent-700">
                      {idx + 1}
                    </span>
                    {idx !== article.timeline.length - 1 && <span className="mt-1 w-px flex-1 bg-background-300"></span>}
                  </span>
                  <div className="pb-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="font-label text-sm font-600 text-foreground-900">{ev.date}</span>
                      <span className="font-heading text-sm font-600 text-foreground-950">{ev.title}</span>
                    </div>
                    <p className="mt-1.5 text-sm leading-relaxed text-foreground-600">{ev.text}</p>
                  </div>
                </li>
              ))}
            </ol>
          </section>

          <section id="responses" className="mt-10 scroll-mt-24">
            <h2 className="font-heading text-xl font-700 text-foreground-950 md:text-2xl">后续回应与当事人澄清</h2>
            <div className="mt-5 grid gap-4 md:grid-cols-2">
              {article.responses.map((r) => (
                <div key={r.who} className="rounded-lg border border-background-200 bg-background-50 p-5">
                  <div className="flex items-center gap-2">
                    <span className="flex h-9 w-9 items-center justify-center rounded-full bg-secondary-100 text-secondary-700">
                      <i className="ri-chat-quote-line text-lg"></i>
                    </span>
                    <div>
                      <div className="text-sm font-600 text-foreground-950">{r.who}</div>
                      <div className="text-xs text-foreground-500">
                        {r.role} · {r.date}
                      </div>
                    </div>
                  </div>
                  <p className="mt-3 text-sm leading-relaxed text-foreground-700">“{r.text}”</p>
                </div>
              ))}
            </div>
          </section>

          <section id="evidence" className="mt-10 scroll-mt-24">
            <h2 className="font-heading text-xl font-700 text-foreground-950 md:text-2xl">证据与信源</h2>
            <ul className="mt-5 divide-y divide-background-200 overflow-hidden rounded-lg border border-background-200 bg-background-50">
              {article.evidence.map((e) => (
                <li key={e.label} className="flex items-start gap-3 px-5 py-4">
                  <i className="ri-article-line mt-0.5 text-lg text-primary-600"></i>
                  <div>
                    <span className="text-sm font-600 text-foreground-950">{e.label}</span>
                    <p className="mt-1 text-sm leading-relaxed text-foreground-600">{e.detail}</p>
                  </div>
                </li>
              ))}
            </ul>
          </section>

          <div className="mt-8 flex flex-wrap gap-2">
            {article.tags.map((t) => (
              <Link
                key={t}
                to={`/verify?q=${encodeURIComponent(t)}`}
                className="rounded-full border border-background-300 px-3 py-1 text-xs text-foreground-600 transition-colors hover:border-primary-400 hover:text-primary-700"
              >
                #{t}
              </Link>
            ))}
          </div>
        </article>

        <aside className="lg:col-span-1">
          <div className="sticky top-24 space-y-5">
            <nav aria-label="内容目录" className="rounded-lg border border-background-200 bg-background-50 p-5">
              <h2 className="font-heading text-sm font-600 text-foreground-950">本页导航</h2>
              <ul className="mt-3 space-y-2">
                {toc.map((t) => (
                  <li key={t.id}>
                    <a
                      href={`#${t.id}`}
                      className="flex items-center gap-2 text-sm text-foreground-600 transition-colors hover:text-primary-700"
                    >
                      <i className="ri-arrow-right-s-line text-foreground-400"></i>
                      {t.label}
                    </a>
                  </li>
                ))}
              </ul>
            </nav>

            <Related current={article} />

            <div className="rounded-lg bg-primary-950 p-5 text-background-100">
              <h2 className="font-heading text-base font-600 text-background-50">掌握更多线索？</h2>
              <p className="mt-2 text-sm leading-relaxed text-background-200">
                如果你有这条爆料的后续进展或原始材料，欢迎提交，我们会持续更新结论。
              </p>
              <Link
                to="/#submit"
                className="mt-4 inline-block whitespace-nowrap rounded-md bg-primary-500 px-4 py-2 text-sm font-500 text-background-50 transition-colors hover:bg-primary-400"
              >
                前往投稿
              </Link>
            </div>
          </div>
        </aside>
      </div>
    </Layout>
  );
}